/**
 * Track M integrators — apply social displacement u and gravity g.
 *
 * Inertial form (unity-gain smoothing — default-stable with Woods-scale u):
 *   target = α · u + g
 *   v'     = δ · v + (1 − δ) · target
 *   x'     = x + v'
 *
 * δ = 0, α = 1, g = 0  ⇒  v' = u, x' = x_social  (exact kinematic Woods).
 * Steady |v| ≈ |target| for constant u (no 1/(1−δ) blow-up).
 *
 * maxSpeed > 0 clips ‖v'‖ after the update (both useful and testable).
 */

import { accumulateGravity, motionIsIdentity } from "./gravity";
import type { SimParams } from "./params";
import type { SpatialHash2D } from "./spatialHash";

export { motionIsIdentity, motionNeedsHash } from "./gravity";

/** Clip each particle's velocity vector in-place to maxSpeed (no-op if ≤ 0). */
export function clipSpeeds(
  velocities: Float64Array,
  n: number,
  dim: number,
  maxSpeed: number,
): void {
  if (!(maxSpeed > 0)) return;
  const maxSp2 = maxSpeed * maxSpeed;
  for (let i = 0; i < n; i++) {
    const b = i * dim;
    let speed2 = 0;
    for (let d = 0; d < dim; d++) {
      const v = velocities[b + d]!;
      speed2 += v * v;
    }
    if (speed2 > maxSp2) {
      const s = maxSpeed / Math.sqrt(speed2);
      for (let d = 0; d < dim; d++) {
        velocities[b + d]! *= s;
      }
    }
  }
}

/**
 * Compose motion into `outPositions`.
 * On entry: `outPositions` holds x_social from the model; `positions` is x.
 * Writes final x' into `outPositions` and updates `velocities` when inertial.
 */
export function applyMotionLayer(
  positions: Float64Array,
  outPositions: Float64Array,
  velocities: Float64Array,
  gravityOut: Float64Array,
  params: SimParams,
  n: number,
  dim: number,
  opts?: {
    types?: Uint8Array | null;
    spatialHash?: SpatialHash2D | null;
  },
): void {
  if (motionIsIdentity(params)) {
    // outPositions already x_social; leave velocities untouched (usually 0)
    return;
  }

  accumulateGravity(positions, n, dim, params, gravityOut, opts);

  const len = n * dim;
  const inertial =
    params.motionMode === "inertial" && params.model !== "reynolds";

  if (!inertial) {
    // kinematic: x' = x + u + g = x_social + g
    // Optional maxSpeed clips the *step* displacement (u+g), not a latent v.
    const maxSp = params.maxSpeed;
    if (maxSp > 0) {
      for (let i = 0; i < n; i++) {
        const b = i * dim;
        let d2 = 0;
        for (let d = 0; d < dim; d++) {
          const step =
            outPositions[b + d]! - positions[b + d]! + gravityOut[b + d]!;
          velocities[b + d] = step; // scratch: step vector
          d2 += step * step;
        }
        let s = 1;
        if (d2 > maxSp * maxSp) s = maxSp / Math.sqrt(d2);
        for (let d = 0; d < dim; d++) {
          outPositions[b + d] = positions[b + d]! + velocities[b + d]! * s;
        }
      }
    } else {
      for (let i = 0; i < len; i++) {
        outPositions[i] = outPositions[i]! + gravityOut[i]!;
      }
    }
    return;
  }

  // Inertial: unity-gain exponential smooth toward (α u + g)
  const damp = clamp01(params.velocityDamping);
  const gain = params.socialGain;
  const oneMinus = 1 - damp;

  for (let i = 0; i < n; i++) {
    const b = i * dim;
    for (let d = 0; d < dim; d++) {
      const u = outPositions[b + d]! - positions[b + d]!;
      const target = gain * u + gravityOut[b + d]!;
      velocities[b + d] = damp * velocities[b + d]! + oneMinus * target;
    }
  }

  clipSpeeds(velocities, n, dim, params.maxSpeed);

  for (let i = 0; i < n; i++) {
    const b = i * dim;
    for (let d = 0; d < dim; d++) {
      outPositions[b + d] = positions[b + d]! + velocities[b + d]!;
    }
  }
}

function clamp01(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  return x;
}
